import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Invoice, Pagination } from '../types/models/Invoice';
import type { ApiResponseInvoiceList } from '../types/api/ApiResponseInvoice';

export interface UseInvoicesParams {
  page: number;
  pageSize: number;
  q?: string;
  type?: string;
  paye?: string;
  /** Lower date bound, YYYY-MM-DD (inclusive). Validated upstream — pass only complete dates. */
  from?: string;
  /** Upper date bound, YYYY-MM-DD (inclusive). Validated upstream — pass only complete dates. */
  to?: string;
}

interface UseInvoicesReturn {
  invoices: Invoice[];
  /** null until the first successful response — lets the consumer tell "initial load" from "filtered to zero". */
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /clients/invoices filters server-side: free-text `q` (title, note, project number, line
// descriptions — fields the browser never receives), `type`, `paye`, the `from`/`to` date range,
// and page/pageSize. The hook fetches exactly the one requested page and re-runs whenever a
// parameter changes.
export const useInvoices = (params: UseInvoicesParams): UseInvoicesReturn => {
  const { page, pageSize, q, type, paye, from, to } = params;
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const abortRef = useRef<AbortController | null>(null);

  const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  // The backend forwards filters upstream only when non-empty; mirror that and omit empty values.
  if (q?.trim()) {
    qs.set('q', q.trim());
  }
  if (type?.trim()) {
    qs.set('type', type.trim());
  }
  if (paye?.trim()) {
    qs.set('paye', paye.trim());
  }
  if (from?.trim()) {
    qs.set('from', from.trim());
  }
  if (to?.trim()) {
    qs.set('to', to.trim());
  }
  const paramsKey = qs.toString();

  // `loading` is derived, not a plain flag: a parameter change makes it true on the very render
  // that carries the new parameters (settledKey lags), NOT one paint later when the fetch effect
  // fires. The page's empty-state matrix relies on this — with a lagging flag, clearing a
  // zero-result search would flash the false "No invoices yet" state (and unmount the toolbar,
  // dropping input focus) before the unfiltered refetch starts. `inFlight` covers same-parameter
  // refetches, which start synchronously inside refetch() itself.
  const loading = inFlight || settledKey !== paramsKey;

  const fetchInvoices = useCallback(async () => {
    // Typing fires overlapping requests; abort the superseded one so a stale response can never
    // overwrite a newer one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setInFlight(true);
    setError(null);

    try {
      const response = await apiFetch(`/clients/invoices?${paramsKey}`, { signal: controller.signal });
      // Branch on the JSend `body.status` — NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseInvoiceList | null;
      if (!body || body.status !== 'success' || !body.data?.invoices || !body.data.pagination) {
        throw new Error(body?.message ?? 'Failed to load invoices');
      }
      setInvoices(body.data.invoices);
      setPagination(body.data.pagination);
    } catch (err) {
      // Superseded request — its abort can surface as an AbortError from fetch, or (when it lands
      // mid-body-read) as the generic parse fallback above. Either way: no state change.
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      // Leave any previously-loaded invoices in place; the consumer shows the error state, which
      // takes precedence over the table, and a successful refetch replaces the set.
      setError(err instanceof Error ? err.message : 'An error occurred while fetching invoices');
    } finally {
      // Only the live request settles loading state — a superseded one leaves it to its successor.
      if (abortRef.current === controller) {
        setInFlight(false);
        setSettledKey(paramsKey);
      }
    }
  }, [apiFetch, paramsKey]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchInvoices();
    }
  }, [auth.isAuthenticated, fetchInvoices]);

  return { invoices, pagination, loading, error, refetch: fetchInvoices };
};
